import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApiResult } from 'src/app/enums/core/ApiResult';
import { DeliveryCommentModel } from 'src/app/models/delivery/DeliveryCommentModel';
import { DeliveriesRepo } from 'src/app/repositories/deliveries.repo';
import { SnackService } from 'src/app/services/SnackService.service';
import { UserLNG } from 'src/app/shared/models/user/userLNG';
import { UserSessionService } from 'src/app/tinea-components/okta/services/session/user-session.service';
import { Delivery, DeliveryComment } from 'src/swagger-gen/lng/models';
import { BasicConfirmModalComponent } from '../dialog/basic-confirm-modal/basic-confirm-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { BasicModalModel } from '../../models/basic-modal/BasicModalModel';
import { tap } from 'rxjs/operators';

@Component({
selector: 'app-delivery-comment',
  templateUrl: './delivery-comment.component.html',
  styleUrls: ['./delivery-comment.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DeliveryCommentComponent implements OnInit, OnChanges {

  @Input() delivery: Delivery;
  @Output() newCommentInput = new EventEmitter<string>();

  commentHistory: DeliveryCommentModel[] = [];
  editedComment = '';
  newComment: string = '';
  newCommentLoading: boolean = false;

  commentEditionAllowed: boolean = false;

  currentUser: UserLNG;

  constructor(
    private userSessionService: UserSessionService, 
    private snackService: SnackService, 
    private deliveriesRepo: DeliveriesRepo,
    public dialog: MatDialog,
    private translate: TranslateService
  ) { 

  }

  ngOnInit(): void {
    this.currentUser = this.userSessionService.getUser();
    this.commentEditionAllowed = this.currentUser.isAdmin() || this.currentUser.isLogistic();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.delivery && this.delivery) {
      this.commentHistory = this.delivery.comments.map(c => new DeliveryCommentModel(c));
    }
  }

  // EVENT
  onClickEditComment(comment: DeliveryCommentModel){
    if(this.commentEditionAllowed){
      this.editedComment = comment.comment;
      comment.editMode = true;
    }
  }

  onClickCancelEditComment(comment: DeliveryCommentModel){
    comment.comment = this.editedComment;
    this.editedComment = '';
    comment.editMode = false;
  }

  onClickConfirmEditComment(comment: DeliveryCommentModel){
    this.editedComment = '';
    this.editDeliveryComment(comment);
  }

  onClickNewComment(){
    this.createDeliveryComment();
  }

  onChangeNewComment(){
    this.newCommentInput.next(this.newComment);
  }

  onClickDeleteComment(comment: DeliveryCommentModel){

    const modal = new BasicModalModel(
        this.translate.instant('deliveries.modal.deliveryComment.confirmDeleteTitle'),
        this.translate.instant('deliveries.modal.deliveryComment.confirmDeleteMessage'),
        this.translate.instant('deliveries.modal.deliveryComment.confirmDeleteAction')
      );

    this.dialog.open( BasicConfirmModalComponent, {
        data: modal,
        disableClose: true,
        width: '320px'
      }).afterClosed().pipe(
        tap((confirm: boolean) => {
          if (confirm) {
            this.deleteDeliveryComment(comment);
          }
        })
    ).toPromise();
  }

  // METHODS

  // API 
  editDeliveryComment(comment: DeliveryCommentModel){
    comment.loading = true;
    const body = {comment: comment.comment}
    this.deliveriesRepo.editDeliveryComment(this.delivery.uid, comment.uid, body).toPromise()
    .then((deliveryComment: DeliveryComment) => {
      comment.editMode = false;
      comment.loading = false;
    })
    .catch((error)=>{
      console.error(error);
      this.snackService.show(ApiResult.ERROR_UPDATE_DELIVERY);
      comment.loading = false;
    })
  }

  deleteDeliveryComment(comment: DeliveryCommentModel){
    comment.loading = true;
    this.deliveriesRepo.deleteDeliveryComment(this.delivery.uid, comment.uid).toPromise()
    .then(() => {
        const index = this.commentHistory.findIndex(c => c.uid == comment.uid);
      if(index > -1){
        this.commentHistory.splice(index, 1);
      }
      comment.loading = false;
    })
    .catch((error)=>{
      console.error(error);
      this.snackService.show(ApiResult.ERROR_UPDATE_DELIVERY);
      comment.loading = false;
    })
  }

  createDeliveryComment(){
    this.newCommentLoading = true;
    const body = {comment: this.newComment}
    this.deliveriesRepo.createDeliveryComment(this.delivery.uid, body).toPromise()
    .then((deliveryComment: DeliveryComment) => {       
        this.commentHistory.unshift(new DeliveryCommentModel(deliveryComment)); 
        this.newComment = '';
        this.newCommentLoading = false;
    })
    .catch((error)=>{
      console.error(error);
      this.snackService.show(ApiResult.ERROR_UPDATE_DELIVERY);
      this.newCommentLoading = false;
    })
  }

}
